import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import { EmissionPointPicker } from '../../../components/bookkeeping/EmissionPointPicker.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type { Invoice } from '../../../types/bookkeeping.js';

interface LineForm {
  descripcion: string;
  cantidad: string; // ×1000
}

function defaultLine(): LineForm {
  return { descripcion: '', cantidad: '1000' };
}

interface GRDetail {
  id: string;
  number: string;
  transportista_razon_social: string;
  transportista_tax_id: string;
  placa: string;
  dir_partida: string;
  fecha_ini_transporte: string;
  fecha_fin_transporte: string;
  destinatario_razon_social: string;
  destinatario_tax_id: string;
  destinatario_direccion: string;
  motivo_traslado: string;
  sri_status: string | null;
  lines: Array<{ descripcion: string; cantidad: string }>;
}

const fmtQty = (q: string): string => {
  const n = BigInt(q || '0');
  return `${n / 1000n}${n % 1000n === 0n ? '' : `.${String(n % 1000n).padStart(3, '0')}`}`;
};

export function GuiaRemisionPage() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: grId } = useParams<{ id: string }>();

  const today = new Date().toISOString().slice(0, 10);
  const [sourceInvoiceId, setSourceInvoiceId] = useState('');
  const [transportista, setTransportista] = useState('');
  const [transportistaTaxId, setTransportistaTaxId] = useState('');
  const [placa, setPlaca] = useState('');
  const [dirPartida, setDirPartida] = useState('');
  const [fechaIni, setFechaIni] = useState(today);
  const [fechaFin, setFechaFin] = useState(today);
  const [destRazon, setDestRazon] = useState('');
  const [destTaxId, setDestTaxId] = useState('');
  const [destDireccion, setDestDireccion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [emissionPointId, setEmissionPointId] = useState<string | null>(null);
  const [lines, setLines] = useState<LineForm[]>([defaultLine()]);
  const [error, setError] = useState<string | null>(null);

  const detailQ = useQuery({
    queryKey: ['bk-guia-remision', grId],
    queryFn: () => api<GRDetail>(`/bookkeeping/sales/guia-remision/${grId}`),
    enabled: !!activeOrgId && !!grId,
  });

  const { data: invoicesData } = useQuery({
    queryKey: ['bk-invoices-all', activeOrgId],
    queryFn: () => api<{ invoices: Invoice[] }>('/bookkeeping/sales/invoices?limit=100'),
    enabled: !!activeOrgId && !grId,
  });
  const authorizedInvoices = (invoicesData?.invoices ?? []).filter(
    (inv) => inv.sri_status === 'AUTORIZADO',
  );

  const createMut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        transportista_razon_social: transportista,
        transportista_tax_id: transportistaTaxId,
        placa,
        dir_partida: dirPartida,
        fecha_ini_transporte: fechaIni,
        fecha_fin_transporte: fechaFin,
        source_invoice_id: sourceInvoiceId || undefined,
        destinatario_razon_social: destRazon || undefined,
        destinatario_tax_id: destTaxId || undefined,
        destinatario_direccion: destDireccion || undefined,
        motivo_traslado: motivo || undefined,
        emission_point_id: emissionPointId ?? undefined,
        lines: lines.map((l) => ({ descripcion: l.descripcion, cantidad: l.cantidad })),
      });
      return api<{ id: string }>('/bookkeeping/sales/guia-remision', { method: 'POST', body });
    },
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['bk-guias-remision'] });
      nav(`/app/bookkeeping/sales/guia-remision/${created.id}`);
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err.body?.error ?? err.message ?? 'Error');
    },
  });

  function submit() {
    if (lines.length === 0 || lines.some((l) => !l.descripcion)) return setError('no_lines');
    if (!sourceInvoiceId && (!destRazon || !destTaxId || !destDireccion || !motivo))
      return setError('destinatario_required');
    setError(null);
    createMut.mutate();
  }

  // ── Detail mode ──────────────────────────────────────────────
  if (grId) {
    const gr = detailQ.data;
    return (
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">
          {t('guiaRemision.title')} {gr ? gr.number : ''}
        </h1>
        {gr && (
          <>
            <div className="rounded border bg-white p-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="font-semibold">{t('guiaRemision.transportista')}:</span>{' '}
                  {gr.transportista_razon_social} ({gr.transportista_tax_id})
                </div>
                <div>
                  <span className="font-semibold">{t('guiaRemision.placa')}:</span> {gr.placa}
                </div>
                <div>
                  <span className="font-semibold">{t('guiaRemision.dir_partida')}:</span>{' '}
                  {gr.dir_partida}
                </div>
                <div>
                  <span className="font-semibold">
                    {t('guiaRemision.fecha_ini')} / {t('guiaRemision.fecha_fin')}:
                  </span>{' '}
                  {gr.fecha_ini_transporte} — {gr.fecha_fin_transporte}
                </div>
                <div>
                  <span className="font-semibold">{t('guiaRemision.destinatario')}:</span>{' '}
                  {gr.destinatario_razon_social} ({gr.destinatario_tax_id})
                </div>
                <div>
                  <span className="font-semibold">{t('guiaRemision.motivo')}:</span>{' '}
                  {gr.motivo_traslado}
                </div>
              </div>
              <table className="mt-3 w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="px-2 py-1">{t('guiaRemision.descripcion')}</th>
                    <th className="px-2 py-1">{t('guiaRemision.cantidad')}</th>
                  </tr>
                </thead>
                <tbody>
                  {gr.lines.map((l, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable order
                    <tr key={i} className="border-b">
                      <td className="px-2 py-1">{l.descripcion}</td>
                      <td className="px-2 py-1 font-mono">{fmtQty(l.cantidad)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <SriStatusTrack docType="guia-remision" docId={gr.id} />
          </>
        )}
      </div>
    );
  }

  // ── Create mode ──────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('guiaRemision.create')}</h1>

      <div className="grid grid-cols-2 gap-3 rounded border bg-white p-4 text-sm">
        <label>
          {t('guiaRemision.transportista')}
          <input
            type="text"
            value={transportista}
            onChange={(e) => setTransportista(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <label>
          {t('guiaRemision.transportista_tax_id')}
          <input
            type="text"
            value={transportistaTaxId}
            onChange={(e) => setTransportistaTaxId(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1 font-mono"
          />
        </label>
        <label>
          {t('guiaRemision.placa')}
          <input
            type="text"
            value={placa}
            onChange={(e) => setPlaca(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1 font-mono"
          />
        </label>
        <label>
          {t('guiaRemision.dir_partida')}
          <input
            type="text"
            value={dirPartida}
            onChange={(e) => setDirPartida(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <label>
          {t('guiaRemision.fecha_ini')}
          <input
            type="date"
            value={fechaIni}
            onChange={(e) => setFechaIni(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <label>
          {t('guiaRemision.fecha_fin')}
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <EmissionPointPicker value={emissionPointId} onChange={setEmissionPointId} />
      </div>

      <div className="grid grid-cols-2 gap-3 rounded border bg-white p-4 text-sm">
        <label className="col-span-2">
          {t('guiaRemision.source_invoice')}
          <select
            value={sourceInvoiceId}
            onChange={(e) => setSourceInvoiceId(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {authorizedInvoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.number}
              </option>
            ))}
          </select>
        </label>
        {sourceInvoiceId ? (
          <p className="col-span-2 text-xs text-slate-500">{t('guiaRemision.derived_note')}</p>
        ) : (
          <>
            <label>
              {t('guiaRemision.destinatario')}
              <input
                type="text"
                value={destRazon}
                onChange={(e) => setDestRazon(e.target.value)}
                className="mt-1 block w-full rounded border px-2 py-1"
              />
            </label>
            <label>
              {t('guiaRemision.destinatario_tax_id')}
              <input
                type="text"
                value={destTaxId}
                onChange={(e) => setDestTaxId(e.target.value)}
                className="mt-1 block w-full rounded border px-2 py-1 font-mono"
              />
            </label>
            <label>
              {t('guiaRemision.destinatario_direccion')}
              <input
                type="text"
                value={destDireccion}
                onChange={(e) => setDestDireccion(e.target.value)}
                className="mt-1 block w-full rounded border px-2 py-1"
              />
            </label>
            <label>
              {t('guiaRemision.motivo')}
              <input
                type="text"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="mt-1 block w-full rounded border px-2 py-1"
              />
            </label>
          </>
        )}
      </div>

      <div className="rounded border bg-white p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="px-2 py-1">{t('guiaRemision.descripcion')}</th>
              <th className="px-2 py-1">{t('guiaRemision.cantidad')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: stable row order
              <tr key={i} className="border-b">
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.descripcion}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, descripcion: e.target.value } : x)),
                      )
                    }
                    className="w-full rounded border px-2 py-1"
                  />
                </td>
                <td className="px-2 py-1">
                  <input
                    type="text"
                    value={l.cantidad}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)),
                      )
                    }
                    className="w-24 rounded border px-2 py-1 text-right font-mono"
                  />
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    className="text-red-600 hover:underline"
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          type="button"
          onClick={() => setLines((ls) => [...ls, defaultLine()])}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          {t('guiaRemision.add_line')}
        </button>
      </div>

      <div className="flex items-center justify-end rounded border bg-white p-4">
        <button
          type="button"
          onClick={submit}
          disabled={
            createMut.isPending || !transportista || !transportistaTaxId || !placa || !dirPartida
          }
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
        >
          {t('guiaRemision.create')}
        </button>
      </div>
      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {t(`guiaRemision.errors.${error}`, { defaultValue: error })}
        </p>
      )}
    </div>
  );
}
